import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react'
import { isEqual } from 'lodash'

import * as analyticsLib from 'global/lib/analytics/analyticsService'
import { useFormatMessage } from 'global/lib/localization'
import { useAppDispatch } from 'global/redux/toolkit/hooks'
import { WhitelistItem } from 'global/types/redux/settings'

import {
  addWhitelistItem,
  editWhitelistItem,
  reset,
  resetError,
  updateAccessToken
} from 'sen/redux/features/settings/settingsSlice'
import { useAppSelector } from 'sen/redux/toolkit/hooks'

export interface TabsConfig {
  activeTab: string
  tabs: Array<{
    id: SettingsTabs
    onClick: () => void
    label: string
  }>
  vertical: boolean
}

export interface SettingsProps {
  isAllowedSenderTableView: boolean
  isBackButtonVisible: boolean
  onClickBackButton: () => void
  onSave: () => void
  isSaveButtonDisabled: boolean
  pageTitle: string
  isSaveButtonVisible: boolean
  tabsConfig: TabsConfig
  setTabState: Dispatch<SetStateAction<{}>>
  setSettingsState: (
    newState: Partial<{
      activeTab: SettingsTabs
      isSaveButtonDisabled: boolean
      isAllowedSenderTableView: boolean
    }>
  ) => void
}

export enum SettingsTabs {
  SPEAR_PHISHING = 'spear_phishing',
  ATO = 'ato',
  ALLOWED_SENDERS = 'allowed_senders',
  SYSLOG_INTEGRATION = 'syslog_integration'
}

const DEFAULT_TAB = SettingsTabs.SPEAR_PHISHING

const BASE_I18N_KEY = 'sen.app.settings'

export default function useSettingsLogic(): [SettingsProps] {
  const dispatch = useAppDispatch()
  const formatMessage = useFormatMessage(BASE_I18N_KEY)
  const {
    atoNotifyAddress,
    atoAttackAlertAdmin,
    notificationEmail,
    spAttackAlertAdmin,
    spAttackQuarantine
  } = useAppSelector(_stores => ({
    notificationEmail: _stores.accessToken.accessToken?.settings?.notifyAddress || _stores.settings.notificationEmail,
    spAttackAlertAdmin: _stores.accessToken.accessToken?.settings.spAttackAlertAdmin,
    atoNotifyAddress: _stores.accessToken.accessToken?.settings.atoNotifyAddress || _stores.settings.notificationEmail,
    atoAttackAlertAdmin: _stores.accessToken.accessToken?.settings.atoAttackAlertAdmin,
    spAttackQuarantine: _stores.accessToken.accessToken?.settings?.spAttackQuarantine
  }))

  const [tabState, setTabState] = useState<Record<string, any>>({})
  const [settingsState, setSettingsState] = useState({
    activeTab: DEFAULT_TAB,
    isSaveButtonDisabled: true,
    isAllowedSenderTableView: true
  })

  // init
  useEffect(() => {
    // unmount
    return () => {
      dispatch(reset())
    }
  }, [dispatch])

  const tabsConfig = useMemo(
    () => ({
      activeTab: settingsState.activeTab,
      tabs: Object.values(SettingsTabs).map(tab => ({
        id: tab,
        onClick: () => {
          dispatch(resetError())
          setSettingsState({
            ...settingsState,
            activeTab: tab
          })
        },
        label: formatMessage(`tabs.${tab}`)
      })),
      vertical: true
    }),
    [formatMessage, dispatch, settingsState]
  )

  const onSave = useCallback(() => {
    switch (settingsState.activeTab) {
      case SettingsTabs.SPEAR_PHISHING:
        dispatch(resetError())
        if (
          !isEqual(tabState.spAttackAlertAdmin, spAttackAlertAdmin) ||
          !isEqual(tabState.notifyAddress, notificationEmail)
        ) {
          analyticsLib.trackAppEvent(analyticsLib.EVENTS.SP_UPDATE_NOTIFY_EMAIL, {
            spAttackAlertAdmin: tabState.spAttackAlertAdmin,
            notificationEmail: tabState.notifyAddress
          })
        }
        if (!isEqual(tabState.spAttackQuarantine, spAttackQuarantine) && Number(tabState.spAttackQuarantine) === 2) {
          analyticsLib.trackAppEvent(analyticsLib.EVENTS.SP_UPDATE_ACTION_TO_DELETE, {
            spAttackQuarantine: tabState.spAttackQuarantine
          })
        }
        dispatch(updateAccessToken({ settings: tabState }))
        break
      case SettingsTabs.ATO:
        if (
          !isEqual(tabState.atoAttackAlertAdmin, atoAttackAlertAdmin) ||
          !isEqual(tabState.atoNotifyAddress, atoNotifyAddress)
        ) {
          analyticsLib.trackAppEvent(analyticsLib.EVENTS.ATO_UPDATE_NOTIFY_EMAIL, {
            atoAttackAlertAdmin: tabState.atoAttackAlertAdmin,
            atoNotifyAddress: tabState.atoNotifyAddress
          })
        }
        dispatch(resetError())
        dispatch(updateAccessToken({ settings: tabState }))

        break
      case SettingsTabs.ALLOWED_SENDERS: {
        dispatch(resetError())
        const allowedSender = {
          id: tabState.id,
          pattern: tabState.pattern,
          remarks: tabState.remarks
        } as WhitelistItem

        if (allowedSender.id) {
          dispatch(editWhitelistItem(allowedSender))
          return
        }
        analyticsLib.trackAppEvent(analyticsLib.EVENTS.ADD_ALLOWED_SENDER_WHITELIST_ITEM, {
          pattern: allowedSender.pattern,
          remarks: allowedSender.remarks
        })
        dispatch(
          addWhitelistItem({
            pattern: allowedSender.pattern,
            remarks: allowedSender.remarks
          })
        )
        break
      }
      default:
        break
    }
  }, [
    atoAttackAlertAdmin,
    atoNotifyAddress,
    dispatch,
    notificationEmail,
    settingsState.activeTab,
    spAttackAlertAdmin,
    spAttackQuarantine,
    tabState
  ])

  const onClickBackButton = useCallback(() => {
    setSettingsState(prevState => ({
      ...prevState,
      isAllowedSenderTableView: true,
      activeTab: SettingsTabs.ALLOWED_SENDERS
    }))
  }, [])

  const handleSetSettingsState = useCallback(
    (newState: Partial<typeof settingsState>) => {
      setSettingsState(prevState => ({
        ...prevState,
        ...newState
      }))
    },
    [setSettingsState]
  )

  return useMemo(
    () => [
      {
        onClickBackButton,
        isBackButtonVisible: !settingsState.isAllowedSenderTableView && settingsState.activeTab === 'allowed_senders',
        isAllowedSenderTableView: settingsState.isAllowedSenderTableView,
        isSaveButtonDisabled: settingsState.isSaveButtonDisabled,
        onSave,
        pageTitle: formatMessage('page_title'),
        isSaveButtonVisible:
          [SettingsTabs.SPEAR_PHISHING, SettingsTabs.ATO].includes(settingsState.activeTab) ||
          (!settingsState.isAllowedSenderTableView && settingsState.activeTab === SettingsTabs.ALLOWED_SENDERS),
        tabsConfig,
        setTabState,
        setSettingsState: handleSetSettingsState
      }
    ],
    [
      formatMessage,
      handleSetSettingsState,
      onClickBackButton,
      onSave,
      settingsState.activeTab,
      settingsState.isAllowedSenderTableView,
      settingsState.isSaveButtonDisabled,
      tabsConfig
    ]
  )
}
